// COMPLETION-PERF-A1 S4 (Oct 2026) — ONE shared poller for the operational summary.
// All widgets (OperationalDashboard, LiveOperations, weekly my-progress) subscribe to this
// store instead of owning timers: 60 s cadence ±5 s jitter, paused while the tab is hidden,
// immediate refresh on return to visible / manual refresh, 5-min backoff after 2 failures.
import axios from 'axios';
import { dedupedGet } from './requestDedupe';

const API = process.env.REACT_APP_BACKEND_URL || '';
export const POLL_MS = 60000;
export const JITTER_MS = 5000;
export const BACKOFF_MS = 300000;
export const BACKOFF_AFTER_FAILURES = 2;

const state = { data: null, myProgress: null, error: null, loading: false, lastFetchedAt: null, failures: 0, backoff: false };
const listeners = new Set();
let timer = null;
let visListener = null;
let inflight = null;
let wantMyProgress = 0;

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
const emit = () => { listeners.forEach((cb) => { try { cb(state); } catch { /* listener error must not break polling */ } }); };

const nextDelay = () => {
  if (state.backoff) return BACKOFF_MS;
  return POLL_MS + Math.round((Math.random() * 2 - 1) * JITTER_MS);
};

const schedule = () => {
  if (timer) clearTimeout(timer);
  timer = null;
  if (listeners.size === 0 || isHidden()) return;
  timer = setTimeout(() => { timer = null; fetchNow().then(schedule); }, nextDelay());
};

export const fetchNow = async () => {
  if (inflight) return inflight;
  state.loading = true; emit();
  inflight = (async () => {
    try {
      const res = await dedupedGet(`${API}/api/performance/operational-summary`);
      state.data = res.data; state.error = null; state.failures = 0; state.backoff = false;
      state.lastFetchedAt = Date.now();
      if (wantMyProgress > 0 && res.data?.role_view === 'processor') {
        try { state.myProgress = (await axios.get(`${API}/api/performance-goals/my-progress`)).data; } catch { /* optional widget */ }
      }
    } catch (e) {
      state.error = e; state.failures += 1;
      state.backoff = state.failures >= BACKOFF_AFTER_FAILURES;
    } finally {
      state.loading = false; inflight = null; emit();
    }
  })();
  return inflight;
};

// Manual refresh / return-to-visible: clear backoff, fetch immediately, restart cadence.
export const refresh = async () => {
  state.failures = 0; state.backoff = false;
  await fetchNow();
  schedule();
};

const onVisibility = () => {
  if (isHidden()) { if (timer) clearTimeout(timer); timer = null; return; }
  refresh();
};

export const subscribe = (cb, { withMyProgress = false } = {}) => {
  listeners.add(cb);
  if (withMyProgress) wantMyProgress += 1;
  cb(state);
  if (listeners.size === 1) {
    visListener = onVisibility;
    document.addEventListener('visibilitychange', visListener);
    if (!isHidden()) fetchNow().then(schedule);
  } else if (withMyProgress && state.data?.role_view === 'processor' && !state.myProgress) {
    fetchNow();
  }
  return () => {
    listeners.delete(cb);
    if (withMyProgress) wantMyProgress = Math.max(0, wantMyProgress - 1);
    if (listeners.size === 0) {
      if (timer) clearTimeout(timer);
      timer = null;
      if (visListener) document.removeEventListener('visibilitychange', visListener);
      visListener = null;
    }
  };
};

export const getState = () => state;
// test-only introspection
export const _debug = () => ({ listeners: listeners.size, hasTimer: !!timer, hasVisListener: !!visListener, wantMyProgress, inflight: !!inflight });
export const _resetForTests = () => {
  listeners.clear(); if (timer) clearTimeout(timer); timer = null;
  if (visListener) document.removeEventListener('visibilitychange', visListener);
  visListener = null; inflight = null; wantMyProgress = 0;
  Object.assign(state, { data: null, myProgress: null, error: null, loading: false, lastFetchedAt: null, failures: 0, backoff: false });
};
